import { CSSProperties, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import blackFollower from "../assets/images/icons/game/Group 80.svg";
import yellowLeader from "../assets/images/icons/game/Group 81.svg";
import fallenFigure from "../assets/images/icons/game/Group 82.svg";
import fallenYellowFigure from "../assets/images/icons/game/Group 82 (1).svg";
import embracingYellowFigure from "../assets/images/icons/game/Group 83.svg";
import embracingCompanion from "../assets/images/icons/game/Group 84.svg";
import waitingFigure from "../assets/images/icons/game/Group 79.svg";
import sadFigure from "../assets/images/icons/game/sad.svg";
import escapePath from "../assets/images/icons/game/Vector 4.svg";
import embraceHeart from "../assets/images/icons/game/Vector.svg";
import linesOne from "../assets/images/icons/lines-1.svg";
import linesTwo from "../assets/images/icons/lines-2.svg";
import linesThree from "../assets/images/icons/lines-3.svg";
import redButton from "../assets/images/icons/red-button.svg";
import restartIcon from "../assets/images/icons/restart.svg";
import RoomHeader from "../components/RoomHeader";
import { useTranslation } from "../components/Localization";
import { saveGameScore } from "../services/gameScores";
import { usePageGame } from "../hooks/usePageGame";

const YELLOW_STEP = 4;
const BLACK_SPEED = 3.33;
const CATCH_DISTANCE = 1.67;
const STEP_DURATION_MS = 480;
const REQUIRED_STEPS = Math.ceil(100 / YELLOW_STEP);
export const MINIMUM_ESCAPE_TIME_SECONDS = ((REQUIRED_STEPS - 1) * STEP_DURATION_MS) / 1000;

export function escapeScoreFromTime(elapsedSeconds: number) {
  if (elapsedSeconds <= 15) return 15;
  if (elapsedSeconds <= 18) return 12;
  if (elapsedSeconds <= 21) return 9;
  if (elapsedSeconds <= 24) return 6;
  return 3;
}

function pathPosition(progress: number): CSSProperties {
  return { offsetDistance: `${progress}%` };
}

export default function LegacyEscapeGamePage() {
  const { room, t } = useTranslation();
  const { game } = usePageGame("mariaffiek", "escape");
  const translatedRoom = room("legacy");
  const [status, setStatus] = useState<"idle" | "running" | "won" | "lost">("idle");
  const [leaderProgress, setLeaderProgress] = useState(0);
  const [followerProgress, setFollowerProgress] = useState(0);
  const [buttonOffset, setButtonOffset] = useState(0);
  const [elapsedTime, setElapsedTime] = useState<number | null>(null);
  const [resultScore, setResultScore] = useState<number | null>(null);
  const leaderRef = useRef(0);
  const followerRef = useRef(0);
  const startedAtRef = useRef<number | null>(null);
  const lastStepAtRef = useRef<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (status !== "running") return;
    let frame = 0;
    let previousTime = performance.now();
    const advanceFollower = (time: number) => {
      const elapsed = Math.min((time - previousTime) / 1000, 0.1);
      previousTime = time;
      const next = Math.min(100, followerRef.current + BLACK_SPEED * elapsed);
      followerRef.current = next;
      setFollowerProgress(next);
      if (next + CATCH_DISTANCE >= leaderRef.current) {
        const elapsed = startedAtRef.current === null ? 0 : (time - startedAtRef.current) / 1000;
        setElapsedTime(elapsed);
        setResultScore(0);
        setStatus("lost");
        return;
      }
      frame = requestAnimationFrame(advanceFollower);
    };
    frame = requestAnimationFrame(advanceFollower);
    return () => cancelAnimationFrame(frame);
  }, [status]);

  const advanceLeader = () => {
    if (status === "won" || status === "lost") return;
    const now = performance.now();
    if (lastStepAtRef.current !== null && now - lastStepAtRef.current < STEP_DURATION_MS) return;
    if (startedAtRef.current === null) startedAtRef.current = now;
    lastStepAtRef.current = now;
    const next = Math.min(100, leaderRef.current + YELLOW_STEP);
    leaderRef.current = next;
    setLeaderProgress(next);
    setButtonOffset((current) => (current <= 0 ? 1 : -1) * (45 + Math.random() * 60));
    if (next >= 100) {
      const elapsed = (now - startedAtRef.current) / 1000;
      const finalScore = escapeScoreFromTime(elapsed);
      setElapsedTime(elapsed);
      setResultScore(finalScore);
      saveGameScore("escape", finalScore);
      setStatus("won");
    }
    else if (status === "idle") setStatus("running");
  };

  const restart = () => {
    leaderRef.current = 0;
    followerRef.current = 0;
    startedAtRef.current = null;
    lastStepAtRef.current = null;
    setLeaderProgress(0);
    setFollowerProgress(0);
    setButtonOffset(0);
    setElapsedTime(null);
    setResultScore(null);
    setStatus("idle");
  };

  const complete = status === "won";
  const lost = status === "lost";

  if (!game) return <main className="screen legacy-escape-game"><RoomHeader roomId="legacy" /></main>;

  return <main className="screen legacy-escape-game">
    <RoomHeader roomId="legacy" />
    <p className="instruction">{lost ? translatedRoom?.tasks.escapeFailed : translatedRoom?.tasks.escape}</p>
    <div className="escape-board" aria-live="polite">
      <img className="escape-path" src={escapePath} alt={t("escapeGame.route")} />
      {status !== "idle" && <svg className="escape-path-trail" width="279" height="362" viewBox="0 0 279 362" aria-hidden="true">
        <path d="M72.5 1.5H240.5C260.658 1.5 277 17.8416 277 38C277 58.1584 260.658 74.5 240.5 74.5H36.5C17.17 74.5 1.5 90.17 1.5 109.5C1.5 128.83 17.17 144.5 36.5 144.5H241C260.882 144.5 277 160.618 277 180.5C277 200.382 260.882 216.5 241 216.5H37.5C17.6177 216.5 1.5 232.618 1.5 252.5C1.5 272.382 17.6178 288.5 37.5 288.5H101.5C121.382 288.5 137.5 304.618 137.5 324.5C137.5 344.382 153.618 360.5 173.5 360.5H277" pathLength="100" style={{ strokeDasharray: `${followerProgress} 100` }} />
      </svg>}
      {status === "idle" && <>
        <img className="escape-start-figure escape-start-yellow" src={yellowLeader} alt={t("escapeGame.escapingFigure")} />
        <img className="escape-start-figure escape-start-black" src={blackFollower} alt={t("escapeGame.follower")} />
        <img className="escape-waiting-figure" src={waitingFigure} alt={t("escapeGame.waitingFigure")} />
      </>}
      {status === "running" && <>
        <img className="escape-moving-figure escape-yellow-leader" style={pathPosition(leaderProgress)} src={yellowLeader} alt={t("escapeGame.escapingFigure")} />
        <img className="escape-moving-figure escape-black-follower" style={pathPosition(followerProgress)} src={blackFollower} alt={t("escapeGame.follower")} />
        <img className="escape-waiting-figure" src={waitingFigure} alt={t("escapeGame.waitingFigure")} />
      </>}
      {complete && <>
        <img className="escape-moving-figure escape-fallen-figure" style={pathPosition(followerProgress)} src={fallenFigure} alt={t("escapeGame.fallenFigure")} />
        <div className="escape-embrace" aria-label={t("escapeGame.embrace")}><img className="escape-embrace-heart" src={embraceHeart} alt="" /><img src={embracingYellowFigure} alt="" /><img src={embracingCompanion} alt="" /></div>
      </>}
      {lost && <>
        <img className="escape-moving-figure escape-black-follower" style={pathPosition(followerProgress)} src={blackFollower} alt={t("escapeGame.catchingFigure")} />
        <img className="escape-moving-figure escape-fallen-figure escape-fallen-yellow" style={pathPosition(leaderProgress)} src={fallenYellowFigure} alt={t("escapeGame.fallenYellowFigure")} />
        <img className="escape-waiting-figure escape-sad-figure" src={sadFigure} alt={t("escapeGame.sadFigure")} />
      </>}
    </div>
    {(status === "idle" || status === "running") && <button className="escape-step-button" style={{ "--escape-button-offset": `${buttonOffset}px` } as CSSProperties} type="button" onClick={advanceLeader} aria-label={t("escapeGame.advance")}>
      <img className="escape-red-button" src={redButton} alt="" />
      <img className="escape-button-lines escape-button-lines-one" src={linesOne} alt="" />
      <img className="escape-button-lines escape-button-lines-two" src={linesTwo} alt="" />
      <img className="escape-button-lines escape-button-lines-three" src={linesThree} alt="" />
    </button>}
    {(complete || lost) && <div className="escape-result-backdrop">
      <section className={`escape-result-modal ${lost ? "is-failure" : "is-success"}`} role="dialog" aria-modal="true" aria-labelledby="escape-result-title">
        {complete ? <>
          <h2 id="escape-result-title">{t("escapeGame.success")}</h2>
          <div className="escape-result-stats">
            <span>{t("escapeGame.elapsedTime", { time: elapsedTime?.toFixed(1) ?? "0.0" })}</span>
            <strong>{t("escapeGame.score", { score: resultScore ?? 0, max: 15 })}</strong>
          </div>
          <div className="escape-result-actions">
            <button type="button" onClick={restart} aria-label={t("escapeGame.retry")}>
              <img className="escape-result-restart" src={restartIcon} alt="" />
              <span>{t("escapeGame.retry")}</span>
            </button>
            <button type="button" onClick={() => navigate("/mariaffiek/telefon")} aria-label={t("common.next")}>
              <img className="escape-result-heart" src={embraceHeart} alt="" />
              <span>{t("common.next")}</span>
            </button>
          </div>
        </> : <>
          <h2 id="escape-result-title">{t("escapeGame.failure")}</h2>
          <div className="escape-result-stats">
            <span>{t("escapeGame.elapsedTime", { time: elapsedTime?.toFixed(1) ?? "0.0" })}</span>
            <strong>{t("escapeGame.score", { score: 0, max: 15 })}</strong>
          </div>
          <button type="button" onClick={restart} aria-label={t("escapeGame.retry")}>
            <img className="escape-result-restart" src={restartIcon} alt="" />
            <span>{t("escapeGame.retry")}</span>
          </button>
        </>}
      </section>
    </div>}
  </main>;
}
